import type { DeviceGeo, DeviceInfo, DeviceKind } from '@troia-v3/types';
import React from 'react';

import { Button } from '@/shared/components/ui/Button';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmpty,
  DataTableHeadCell,
  DataTableHeader,
  DataTableRow,
} from '@/shared/components/ui/DataTable';
import { Icon, type IconName } from '@/shared/icons';

/**
 * Linha da tabela de dispositivos — vocabulário comum a operadores
 * (`users.devices[]`) e contatos (`contact-devices`), para que a mesma tabela
 * sirva "Minha Conta → Segurança" e a aba "Dispositivos" do contato.
 */
export interface DeviceRow extends DeviceInfo {
  /** Chave estável (deviceId do cliente). */
  deviceId: string;
  /** Legado dos devices de operador: string livre vinda do cliente ('Chrome'). */
  browserLabel?: string;
  createdAt?: string;
  lastActiveAt?: string;
  /** Selos exibidos ao lado do nome (ex.: "Este dispositivo", "App instalado"). */
  badges?: Array<{ text: string; color: string; bg: string }>;
  /** Texto da coluna Origem (contato: canal/site; operador: fica vazio). */
  origin?: string;
}

interface DevicesTableProps {
  devices: DeviceRow[];
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage: string;
  /** Rótulo da coluna de data mais à direita (ex.: "Último login", "Último acesso"). */
  lastActiveLabel?: string;
  /** Mostra a coluna Origem (só faz sentido para dispositivos de contato). */
  showOrigin?: boolean;
  onDelete?: (device: DeviceRow) => void;
  deleteDisabled?: boolean;
  testId?: string;
}

const KIND_ICON: Record<DeviceKind, IconName> = {
  smartphone: 'smartphone',
  tablet: 'tablet',
  desktop: 'monitor',
  unknown: 'monitor',
};

const OS_LABEL: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  unknown: '',
};

const BROWSER_LABEL: Record<string, string> = {
  chrome: 'Chrome',
  safari: 'Safari',
  firefox: 'Firefox',
  edge: 'Edge',
  samsung: 'Samsung Internet',
  opera: 'Opera',
  unknown: '',
};

const formatDate = (iso?: string): string =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/** "Chrome 126 · macOS 15.5" — versões entram só quando conhecidas. */
const describeSoftware = (device: DeviceRow): string => {
  const browser = device.browserLabel ?? BROWSER_LABEL[device.browser ?? 'unknown'] ?? '';
  const browserPart = [browser, device.browserVersion?.split('.')[0]].filter(Boolean).join(' ');
  const osPart = [OS_LABEL[device.os ?? 'unknown'] ?? '', device.osVersion].filter(Boolean).join(' ');
  return [browserPart, osPart].filter(Boolean).join(' · ') || '—';
};

const describeLocation = (location?: DeviceGeo): string => {
  if (!location) return '—';
  return [location.city, location.region, location.country].filter(Boolean).join(', ') || '—';
};

const Pill: React.FC<{ text: string; color: string; bg: string }> = ({ text, color, bg }) => (
  <span className="px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap" style={{ color, backgroundColor: bg }}>
    {text}
  </span>
);

/**
 * Tabela de dispositivos.
 *
 * Desktop = tabela; mobile = cards (regra de listagens do projeto). O ícone sai
 * de `deviceKind`, que vem do parse de User-Agent + Client Hints no servidor —
 * antes a tela decidia por `platform === 'web'` e mostrava computador pra tudo.
 */
export const DevicesTable: React.FC<DevicesTableProps> = ({
  devices,
  isLoading = false,
  isError = false,
  emptyMessage,
  lastActiveLabel = 'Último acesso',
  showOrigin = false,
  onDelete,
  deleteDisabled = false,
  testId = 'devices-table',
}) => {
  if (isLoading) {
    return (
      <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }} data-testid={`${testId}-loading`}>
        Carregando dispositivos…
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-sm" style={{ color: 'var(--theme-error-color)' }} data-testid={`${testId}-error`}>
        Não foi possível carregar os dispositivos.
      </p>
    );
  }

  if (devices.length === 0) {
    return (
      <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }} data-testid={`${testId}-empty`}>
        {emptyMessage}
      </p>
    );
  }

  /**
   * Registros anteriores ao enriquecimento (2026-08-11) não têm modelo nem SO —
   * `??` não serve aqui porque `OS_LABEL.unknown` é string vazia e o título
   * ficaria em branco. Cai para navegador e, no limite, para um rótulo neutro.
   */
  const deviceTitle = (device: DeviceRow): string =>
    device.model
    || OS_LABEL[device.os ?? 'unknown']
    || device.browserLabel
    || BROWSER_LABEL[device.browser ?? 'unknown']
    || 'Dispositivo';

  return (
    <div data-testid={testId}>
      {/* Desktop: tabela */}
      <div className="hidden md:block">
        <DataTable>
          <DataTableHeader>
            <DataTableHeadCell>Dispositivo</DataTableHeadCell>
            <DataTableHeadCell>Navegador · Sistema</DataTableHeadCell>
            {showOrigin && <DataTableHeadCell>Origem</DataTableHeadCell>}
            <DataTableHeadCell>Localização</DataTableHeadCell>
            <DataTableHeadCell>IP</DataTableHeadCell>
            <DataTableHeadCell>{lastActiveLabel}</DataTableHeadCell>
            {onDelete && <DataTableHeadCell align="right">Ações</DataTableHeadCell>}
          </DataTableHeader>
          <DataTableBody>
            {devices.length === 0 && <DataTableEmpty colSpan={7} message={emptyMessage} />}
            {devices.map((device) => (
              <DataTableRow key={device.deviceId} data-testid={`${testId}-row-${device.deviceId}`}>
                <DataTableCell>
                  <div className="flex items-center gap-2.5">
                    <Icon
                      name={KIND_ICON[device.deviceKind ?? 'unknown']}
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: 'var(--theme-muted-text)' }}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                        {deviceTitle(device)}
                      </div>
                      {(device.badges?.length ?? 0) > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {device.badges?.map((badge) => (
                            <Pill key={badge.text} {...badge} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </DataTableCell>
                <DataTableCell>
                  <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                    {describeSoftware(device)}
                  </span>
                </DataTableCell>
                {showOrigin && (
                  <DataTableCell>
                    <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                      {device.origin ?? '—'}
                    </span>
                  </DataTableCell>
                )}
                <DataTableCell>
                  <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                    {describeLocation(device.location)}
                  </span>
                </DataTableCell>
                <DataTableCell>
                  <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                    {device.lastIp ?? '—'}
                  </span>
                </DataTableCell>
                <DataTableCell>
                  <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                    {formatDate(device.lastActiveAt)}
                  </span>
                </DataTableCell>
                {onDelete && (
                  <DataTableCell align="right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { onDelete(device); }}
                      disabled={deleteDisabled}
                      data-testid={`${testId}-delete-${device.deviceId}`}
                      aria-label="Excluir dispositivo"
                    >
                      <Icon name="trash" className="w-4 h-4" style={{ color: 'var(--theme-error-color)' }} />
                    </Button>
                  </DataTableCell>
                )}
              </DataTableRow>
            ))}
          </DataTableBody>
        </DataTable>
      </div>

      {/* Mobile: cards */}
      <div className="md:hidden space-y-3">
        {devices.map((device) => (
          <div
            key={device.deviceId}
            data-testid={`${testId}-card-${device.deviceId}`}
            className="rounded-lg p-3"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
            }}
          >
            <div className="flex items-start gap-2.5">
              <Icon
                name={KIND_ICON[device.deviceKind ?? 'unknown']}
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--theme-muted-text)' }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                    {deviceTitle(device)}
                  </span>
                  {device.badges?.map((badge) => (
                    <Pill key={badge.text} {...badge} />
                  ))}
                </div>
                <div className="text-xs mt-1 space-y-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                  <div>{describeSoftware(device)}</div>
                  {showOrigin && <div>{device.origin ?? '—'}</div>}
                  <div>
                    {describeLocation(device.location)}
                    {device.lastIp ? ` · ${device.lastIp}` : ''}
                  </div>
                  <div>{lastActiveLabel}: {formatDate(device.lastActiveAt)}</div>
                </div>
              </div>
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { onDelete(device); }}
                  disabled={deleteDisabled}
                  data-testid={`${testId}-card-delete-${device.deviceId}`}
                  aria-label="Excluir dispositivo"
                >
                  <Icon name="trash" className="w-4 h-4" style={{ color: 'var(--theme-error-color)' }} />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
